import type { ApplicantStatus } from "@/features/recruitment/schemas/applicant.schema";

/**
 * Plain words for the applicant vocabularies, in one place for the same reason
 * `requisition-status.ts` holds the requisition ones: the board, the menu, the
 * detail header and the toasts all name the same six statuses, and three of
 * them inventing their own wording is how "On hold" and "Hold" end up on the
 * same screen.
 */

export const APPLICANT_STATUS_LABEL: Record<ApplicantStatus, string> = {
  open: "Open",
  replied: "Replied",
  shortlisted: "Shortlisted",
  hold: "On hold",
  accepted: "Accepted",
  rejected: "Rejected",
};

// The status-to-tone map that used to live here is gone. `recruitment.taxonomy.ts`
// owns tone now, and `RecruitmentStatusBadge` is the one component that renders
// it. Two maps disagreed: this one made `hold` a quiet grey, the taxonomy makes
// it amber, and a parked candidate is somebody owed a decision. Labels stay
// here, because one place naming the six statuses is the point of this file.

const SOURCE_LABEL: Record<string, string> = {
  website: "Website",
  referral: "Referral",
  linkedin: "LinkedIn",
  agency: "Agency",
  walk_in: "Walk in",
  other: "Other",
};

/// An unknown source (REC-012 may widen the vocabulary before this file hears
/// about it) reads as itself with its underscores opened out, never as blank.
export function applicantSourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source.replace(/_/g, " ");
}

const OFFER_STATUS_LABEL: Record<string, string> = {
  awaiting_response: "Offer out",
  accepted: "Offer accepted",
  rejected: "Offer declined",
  withdrawn: "Offer withdrawn",
};

export function offerStatusLabel(status: string): string {
  return OFFER_STATUS_LABEL[status] ?? status.replace(/_/g, " ");
}

const INTERVIEW_STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  under_review: "Under review",
  cleared: "Cleared",
  rejected: "Rejected",
};

export function interviewStatusLabel(status: string): string {
  return INTERVIEW_STATUS_LABEL[status] ?? status.replace(/_/g, " ");
}
